// Typed URL search params for the inventory routes that are DRILL TARGETS.
//
// §4.9's ATP inspector promises "drill links into each term" and §4.1's result
// cards are reachable from a scan — both need a filter to survive a navigation.
// TanStack Router only types a `<Link search=…>` when the target route declares
// `validateSearch`, so the shapes live here (imported by router.tsx for the
// route definitions and by the screens for their initial state) rather than
// being smuggled through `window.location`.
//
// Validation is defensive by design: an unknown or malformed param is dropped,
// never thrown on. A shared link with a stale filter must still open the screen.

import {
  ADJUST_DIRECTIONS,
  counterForDirection,
  isWireQuantity,
  reasonForDirection,
} from "../lib/adjust";

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export interface MovementsSearch {
  readonly product_id?: string | undefined;
  readonly lot_id?: string | undefined;
  readonly stock_unit_id?: string | undefined;
  readonly location_id?: string | undefined;
  readonly movement_type?: string | undefined;
  readonly source_doc_type?: string | undefined;
  /** §4.12's request page links a POSTED request to the movement it wrote:
   * `source_doc_type=manual_adjustment&source_doc_id={request id}` (§3.9). */
  readonly source_doc_id?: string | undefined;
}

export function validateMovementsSearch(search: Record<string, unknown>): MovementsSearch {
  return {
    product_id: str(search["product_id"]),
    lot_id: str(search["lot_id"]),
    stock_unit_id: str(search["stock_unit_id"]),
    location_id: str(search["location_id"]),
    movement_type: str(search["movement_type"]),
    source_doc_type: str(search["source_doc_type"]),
    source_doc_id: str(search["source_doc_id"]),
  };
}

export interface TransfersSearch {
  readonly status?: string | undefined;
}

export function validateTransfersSearch(search: Record<string, unknown>): TransfersSearch {
  return { status: str(search["status"]) };
}

export interface LookupSearch {
  readonly q?: string | undefined;
}

export function validateLookupSearch(search: Record<string, unknown>): LookupSearch {
  return { q: str(search["q"]) };
}

// ---- §4.12 Stock Adjustment (D-315) ------------------------------------
//
// Every entry to the form carries what its screen already knows: §4.1's bulk
// row and §4.6's stock row name a product, a batch and a place; §4.3 names a
// place; spec 04's S2 and S7 sentences name the order, the component, its
// batch or container, and what the order says is still out (as a hint, never
// as the quantity). A param that is malformed, or that contradicts another (a
// counter or a reason the direction does not offer), is DROPPED — the form
// then asks for it rather than posting something the link did not mean. The
// vocabulary is lib/adjust.ts's, imported, so a reason added there reaches
// every link.

const ADJUST_FROM = ["lookup", "lot", "location", "approvals", "work_order", "conflict"] as const;
export type AdjustFrom = (typeof ADJUST_FROM)[number];

export interface AdjustSearch {
  readonly facility_id?: string | undefined;
  readonly product_id?: string | undefined;
  readonly lot_id?: string | undefined;
  readonly location_id?: string | undefined;
  readonly direction?: "increase" | "decrease" | undefined;
  readonly counter?: "found" | "wip" | "scrap" | undefined;
  readonly quantity?: string | undefined;
  readonly uom?: string | undefined;
  readonly reason?: string | undefined;
  readonly work_order_id?: string | undefined;
  /** The container the material came off (a D-308 row that drew a drum, or a
   * floor return that scanned one by its label). The form reads it and
   * follows D-308 round 2: loose only when the container holds no stock. */
  readonly unit_id?: string | undefined;
  /** The same, when the floor scanned a serial rather than a label. */
  readonly unit_serial?: string | undefined;
  /** What the order says is still out, shown as a hint and never typed in
   * for the person: they enter what they weighed (the #282 review). */
  readonly expected_qty?: string | undefined;
  /** Which screen the person came from, for the back link. */
  readonly from?: AdjustFrom | undefined;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

export function validateAdjustSearch(search: Record<string, unknown>): AdjustSearch {
  const direction = oneOf(search["direction"], ADJUST_DIRECTIONS);
  const quantityRaw = str(search["quantity"]);
  const expectedRaw = str(search["expected_qty"]);
  return {
    facility_id: str(search["facility_id"]),
    product_id: str(search["product_id"]),
    lot_id: str(search["lot_id"]),
    location_id: str(search["location_id"]),
    direction,
    counter:
      direction === undefined ? undefined : counterForDirection(direction, str(search["counter"])),
    quantity: quantityRaw !== undefined && isWireQuantity(quantityRaw) ? quantityRaw : undefined,
    uom: str(search["uom"]),
    reason:
      direction === undefined ? undefined : reasonForDirection(direction, str(search["reason"])),
    work_order_id: str(search["work_order_id"]),
    unit_id: str(search["unit_id"]),
    unit_serial: str(search["unit_serial"]),
    expected_qty:
      expectedRaw !== undefined && isWireQuantity(expectedRaw) ? expectedRaw : undefined,
    from: oneOf(search["from"], ADJUST_FROM),
  };
}
